import {
  computeBuyingPower,
  pickPerpFeeStructure,
  resolveOpeningFeeRate,
} from "../TradeForm/PerpsTradeForm/perpFees";
import {
  GetAccount200Response,
  GetMarkets200Response,
  GetOrders200Response,
  OrderTypeResponse,
  SideResponse,
} from "../../codegen-api";
import {
  MIN_ORDER_VALUE,
  getPerpsMaxLeverage,
} from "../../constants/precision/form";
import { utils } from "ethers";
import { AssetResponse } from "../../utils/asset";
import { toTokenUnits } from "../../utils/decimalString";
import {
  alignPriceToStep,
  floorToStepString,
  roundToStepSize,
} from "../../utils/format";
import { IContractPriceStep } from "../../utils/instruments";
import { knownPrice } from "../../utils/unknownNumbers";
import { ToastEnum, ToastStatusEnum } from "../../utils/toast";

/** The side of the grid a price may round to without costing the maker: a buy
 * never pays more than entered, a sell never sells for less. */
export const editPriceDirection = (side?: SideResponse): "down" | "up" =>
  side === SideResponse.Sell ? "up" : "down";

/**
 * What is left of an order, floored to `amount_step`.
 *
 * This used to be `Math.max(price_step, unfilled)`: a PRICE step as a SIZE
 * floor, so 0.001 BTC unfilled on a market with `price_step` 1 was edited as a
 * 1 BTC order.
 */
export function computeUnfilledAmount(
  order: Pick<GetOrders200Response, "amount" | "filled"> | undefined,
  contractPriceStep: IContractPriceStep
): string {
  // Exact subtraction in 1e18 units: in floats 47.41 - 44.82 is
  // 2.5899999999999963, which floored a whole step short (2.58).
  const amountUnits = toTokenUnits(order?.amount, 18);
  const filledUnits = toTokenUnits(order?.filled || "0", 18);
  const unfilled =
    amountUnits && filledUnits && amountUnits.gt(filledUnits)
      ? utils.formatUnits(amountUnits.sub(filledUnits), 18)
      : "0";
  return floorToStepString(
    unfilled,
    contractPriceStep.amount_step,
    contractPriceStep.amount_precision
  );
}

/**
 * A typed USD figure snapped for display on blur: floored to the cent, so it
 * never exceeds what was typed and repeating it changes nothing. (Snapping to
 * the step-floored size's value, floored again, lost a cent on every blur.)
 */
export const floorUsdFieldValue = (typed: string): number | "" =>
  typed.trim() === "" ? "" : roundToStepSize(typed, 0.01, 2);

/**
 * The USD figure for a size, floored to the cent. `currency(...).value` rounded
 * half-up, and converting that back to contracts grew the order.
 */
export const sizeToUsdValue = (price: unknown, size: unknown): number => {
  const value = Number(price) * Number(size);
  return Number.isFinite(value) && value > 0
    ? roundToStepSize(value, 0.01, 2)
    : 0;
};

/**
 * The largest step-aligned size that `usdValue` pays for at `price`: never worth
 * more than the USD entered, not even by a fraction of a cent.
 *
 * The pre-filled USD figure is floored to the cent, so converting it back can
 * fall one step short of the order (0.001 BTC at 65432.17 shows $65.43, which
 * buys 0 on a 0.001 step). That case is handled by `computeEditContractSize`
 * keeping the order's own size while the pre-filled figures are untouched, not
 * by letting this conversion overshoot.
 */
export function usdToContractSize(
  usdValue: unknown,
  price: unknown,
  amountStep: number,
  amountPrecision: number
): string {
  const usd = Number(usdValue);
  const p = Number(price);
  if (!(usd > 0) || !Number.isFinite(p) || p <= 0) {
    return floorToStepString(0, amountStep, amountPrecision);
  }
  return floorToStepString(usd / p, amountStep, amountPrecision);
}

/**
 * Contracts the edited order is for, as plain decimal text floored to
 * `amount_step`. In USD mode an empty or zero price gives "0", never the
 * Infinity / NaN that `amount / price` produced.
 */
export function computeEditContractSize({
  amount,
  price,
  isUsd,
  contractPriceStep,
  seed,
}: {
  amount: unknown;
  price: unknown;
  isUsd: boolean;
  contractPriceStep: IContractPriceStep;
  /**
   * The USD figure the modal pre-filled from the order's unfilled size, and the
   * price it used. While both are untouched the order's own size is sent, so
   * saving without editing the size never shrinks the order.
   */
  seed?: { usd: number; price: unknown; size: string };
}): string {
  const { amount_step, amount_precision } = contractPriceStep;
  if (isUsd) {
    if (
      seed &&
      Number(seed.size) > 0 &&
      Number(amount) === seed.usd &&
      Number(price) === Number(seed.price)
    ) {
      return seed.size;
    }
    return usdToContractSize(amount, price, amount_step, amount_precision);
  }
  const size = Number(amount);
  return floorToStepString(
    Number.isFinite(size) && size > 0 ? size : 0,
    amount_step,
    amount_precision
  );
}

export async function submitEditOrder({
  order,
  editOrder,
  contractSize,
  contractPriceStep,
  price,
  triggerPrice,
  playSound,
  addToast,
  t,
  onHide,
  apiError,
  setErrorMessage,
  setLoading,
}: {
  order?: GetOrders200Response;
  editOrder: (orderId: string, newOrder: any) => Promise<any>;
  contractSize: string;
  contractPriceStep: IContractPriceStep;
  price: any;
  triggerPrice: any;
  playSound: (...args: any[]) => any;
  addToast: (...args: any[]) => any;
  t: (key: any, options?: any) => any;
  onHide?: () => void;
  apiError: (key: any, options?: any) => any;
  setErrorMessage: (value?: string) => void;
  setLoading: (value: boolean) => void;
}) {
  if (!order) {
    return;
  }

  try {
    setErrorMessage(undefined);
    setLoading(true);

    if (!(Number(contractSize) > 0)) {
      throw new Error(t("edit_order_error"));
    }
    // A limit price submitted without a blur was never snapped to the grid. The
    // trigger is sent as entered: rounding it on submit could move a valid stop
    // to the wrong side of the mark after validation had passed.
    const { price_step, price_precision } = contractPriceStep;
    const alignedPrice = alignPriceToStep(
      price,
      price_step,
      price_precision,
      editPriceDirection(order.side)
    );

    await editOrder(order.order_id, {
      instrument: order.instrument_id,
      amount: contractSize,
      side: order.side,
      price:
        order.order_type === OrderTypeResponse.Limit ? alignedPrice : undefined,
      orderType: order.order_type,
      stop: order.stop,
      trigger: triggerPrice || undefined,
    });

    // onHide?.();
    // TODO: - Show bid edited toasts
    playSound("order_placed", true);
    addToast(
      {
        type: ToastEnum.SIMPLE,
        header: t("order_edited_header"),
        subheader: t("order_edited_subheader"),
        status: ToastStatusEnum.SUCCESS,
      },
      4000
    );
    onHide?.();
  } catch (error: any) {
    setErrorMessage(apiError(error.message) || t("edit_order_error"));
  } finally {
    setLoading(false);
  }
}

/**
 * USD mode: whether the order that will actually be sent (`contractSize` at
 * `price`) is under the minimum value. The figure typed is not what is sent —
 * the size is floored to `amount_step` — so it is not what gets checked. The
 * worth is rounded to the cent before comparing, as the old blur snap did.
 */
export const isUsdEditValueTooSmall = (
  contractSize: unknown,
  price: unknown
): boolean => {
  const worth = Number(contractSize) * Number(price);
  if (!Number.isFinite(worth) || worth <= 0) return true;
  return Number(worth.toFixed(2)) < MIN_ORDER_VALUE;
};

export function verifyOrderValueTooSmallImpl(
  size: string,
  {
    isOption,
    perpAsset,
    order,
    instrument,
    price,
  }: {
    isOption: boolean;
    perpAsset?: AssetResponse;
    order?: GetOrders200Response;
    instrument?: GetMarkets200Response;
    price: any;
  }
): boolean {
  // Perps and asset is USD
  if (!isOption && !perpAsset) {
    return Number(size || 0) >= MIN_ORDER_VALUE;
  }

  // If is market order, use either trigger price of mark price. An unknown
  // mark (null or "0") gives no price, so the minimum-value check cannot run.
  const p =
    order?.order_type === OrderTypeResponse.Market
      ? knownPrice(order?.trigger) ?? knownPrice(instrument?.mark_price)
      : price;

  if (p && size) {
    return Number(size || 0) * Number(p || 0) >= MIN_ORDER_VALUE;
  }
  return true;
}

export function computeMaxLeverage({
  accountData,
  instrument,
}: {
  accountData?: GetAccount200Response;
  instrument?: GetMarkets200Response;
}): number {
  const accountLeverage = accountData?.leverages?.find(
    (lev) => lev.instrument_id === instrument?.instrument_id
  )?.leverage;
  const lev = accountLeverage
    ? Number(accountLeverage)
    : Number(instrument?.max_leverage);
  return lev || 0;
}

/**
 * What this account could still open on this market, in USD.
 *
 * Was `available_balance × maxLeverage`: the whole portfolio value geared to
 * the market's ceiling, ignoring fees, the margin already committed and the
 * leverage actually selected — a number the exchange would never accept. It is
 * now the trade form's own figure (withdrawable ÷ (1/leverage + fee)), which
 * answers 0 for "not known" and is shown as "—" rather than as money.
 */
export function computeEditBuyingPower({
  accountData,
  maxLeverage,
  instrument,
}: {
  accountData?: GetAccount200Response;
  maxLeverage: number;
  instrument?: GetMarkets200Response;
}): number {
  if (!accountData) return 0;
  const leverage =
    maxLeverage || getPerpsMaxLeverage(instrument?.instrument_name) || 1;
  const feeRate = resolveOpeningFeeRate(
    pickPerpFeeStructure(accountData.fee_structures, instrument?.underlying_asset)
  );
  return computeBuyingPower(accountData, leverage, feeRate, false);
}

export function computeTotalValue({
  order,
  instrument,
  price,
  amount,
  contractSize,
}: {
  order?: GetOrders200Response;
  instrument?: GetMarkets200Response;
  price: any;
  amount: any;
  contractSize: number;
}): number {
  // If is market order, use either trigger price of mark price. Unknown mark:
  // no total (0 renders "---" in buildTradeInfo), never a total at $0.
  const p =
    order?.order_type === OrderTypeResponse.Market
      ? knownPrice(order?.trigger) ?? knownPrice(instrument?.mark_price)
      : price;

  if (Number(amount) && Number(p)) {
    return contractSize * Number(p);
  }
  return 0;
}
